import { CheckCircle2, LockKeyhole, Target } from "lucide-react";
import Button from "../components/Button";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import { CUSTOMER_PORTAL_URL } from "../lib/portal";

const pricingPrinciples = [
  [
    Target,
    "Built around your needs",
    "Every service is shaped around the scope, complexity, timing and level of coordination your needs require.",
  ],
  [
    CheckCircle2,
    "Clear before commitment",
    "You see what is included, the delivery path and the milestones that matter before work moves forward.",
  ],
  [
    LockKeyhole,
    "Secure from start to finish",
    "Payments, progress and supporting records are handled through a structured customer experience.",
  ],
];

export function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Clear scope. Transparent milestones. No surprises."
        copy="Pricing is shaped around the service you need and the result you want to achieve. The customer portal keeps the details, progress and payments together."
      />

      <section id="content" className="section-space bg-white">
        <div className="container">
          <SectionHeading
            eyebrow="Our pricing approach"
            title="A considered price for clearly defined work."
            copy="The Bridge brings structure to complex services, so you can make an informed decision before execution begins."
            align="center"
          />
          <div className="mt-12 grid divide-y divide-ink/10 border-y border-ink/10 md:grid-cols-3 md:divide-x md:divide-y-0">
            {pricingPrinciples.map(([Icon, title, copy], index) => (
              <Reveal key={title} delay={index * 70} className="h-full">
                <div className="h-full px-7 py-9 sm:px-9 sm:py-10 lg:px-10 lg:py-11">
                  <Icon className="text-ocean" size={28} strokeWidth={2} />
                  <h2 className="mt-7 font-display text-xl font-bold text-ink">
                    {title}
                  </h2>
                  <p className="mt-3 max-w-sm text-sm leading-7 text-slate">
                    {copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space bg-mist">
        <div className="container grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <SectionHeading
            eyebrow="What shapes the price"
            title="The details matter."
            copy="Service pricing reflects the work required to deliver a dependable result—not a one-size-fits-all promise."
          />
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              "The service category and required expertise",
              "The locations, documents and dependencies involved",
              "The delivery timeline and level of coordination",
              "Any third-party costs or payments required to complete the work",
            ].map((item, index) => (
              <Reveal key={item} delay={index * 60}>
                <div className="flex gap-4 border-t border-ink/10 pt-5">
                  <CheckCircle2
                    className="mt-0.5 shrink-0 text-ocean"
                    size={22}
                  />
                  <p className="text-sm leading-7 text-ink">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-bridge text-ink">
        <div className="absolute -right-24 top-[-140px] h-[360px] w-[360px] rounded-full bg-white/20 blur-3xl" />
        <div className="container relative flex flex-col gap-8 py-20 sm:flex-row sm:items-center sm:justify-between lg:py-28">
          <div>
            <p className="eyebrow text-ink/60">Ready to move forward?</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-[-.04em] sm:text-4xl">
              Continue in the customer portal.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-slate">
              Review available services, manage your account and keep every
              milestone in one secure place.
            </p>
          </div>
          <Button href={`${CUSTOMER_PORTAL_URL}/login`} variant="dark">Continue to portal</Button>
        </div>
      </section>
    </>
  );
}
